import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ImageIcon, Loader2, Package, Sparkles, Upload } from 'lucide-react';
import { TaskyButton, TaskyCard, TaskyInput, TaskyAlert, TaskyConfirmDialog } from '../tasky';
import { ProductSelectionModal, ProductSelectionData } from '../image-generation';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';
import ugcAdService, { UgcAdProject, UpdateUgcAdProjectParams } from '../../services/api/ugcAdService';
import { ProductImage } from '../../types/knowledge';
import UgcAdSettingsForm, {
  isValidUgcTargetSeconds,
  joinWordsInput,
  splitWordsInput,
  UgcAdSettingsValue,
} from './UgcAdSettingsForm';
import UgcAdImageKeysField, { UgcAdImageKeyItem } from './UgcAdImageKeysField';

/**
 * ステップ①: 情報登録（song-ad InfoStep 踏襲）。
 *
 * 商品選択・画像・構成タイプ・出演者・尺・追加指示・詳細設定（必須/禁止語・CTA・CTAロゴ）を
 * 1タブに常設する。保存は PATCH /ugc-ads/{id}。
 *
 * 保存には影響グループ別のガードを入れる:
 * - 構成案に効くフィールド（構成タイプ/出演者/尺/追加指示/必須・禁止語）を変更したとき、
 *   シーン（構成案）が既にあるなら確認する
 * - 映像に効くフィールド（商品/画像）を変更したとき、シーンが既にあるなら確認する
 * - タイトル・CTA文言・CTAロゴは無害なので確認不要
 * サーバー側は非破壊（自動削除しない）。作り直しは②構成案の再生成に委ねる。
 */

interface InfoStepProps {
  project: UgcAdProject;
  onSave: (params: UpdateUgcAdProjectParams) => Promise<boolean>;
  /**
   * 出演者候補の生成（非同期job発行）。進捗はポーリングで project.cast_candidates に反映される。
   * 性別・年代はローカルドラフトのため、呼び出し側が生成前に現在値をPATCHしてから発行する
   * （未保存の選択が候補に反映されない問題への対策。レビューP0）。
   */
  onGenerateCastCandidates: (castGender: UgcAdSettingsValue['castGender'], castAge: UgcAdSettingsValue['castAge']) => Promise<void>;
  isCastCandidatesRequesting?: boolean;
}

const toSettingsValue = (project: UgcAdProject): UgcAdSettingsValue => ({
  formatType: project.format_type,
  castGender: project.cast_gender,
  castAge: project.cast_age,
  targetSeconds: isValidUgcTargetSeconds(project.target_seconds) ? project.target_seconds : 30,
  freeText: project.free_text ?? '',
  requiredWordsText: joinWordsInput(project.advanced_settings?.required_words ?? []),
  bannedWordsText: joinWordsInput(project.advanced_settings?.banned_words ?? []),
});

const sortedKey = (keys: string[]): string => JSON.stringify([...keys].sort());

/** 商品画像から `category === 'brand_logo'` のものだけをロゴ候補として抽出する（song-ad と同じ発想） */
const extractBrandLogoImages = (images?: ProductImage[] | null): UgcAdImageKeyItem[] =>
  (images ?? [])
    .filter((img) => img.category === 'brand_logo')
    .map((img) => ({
      key: img.file_path,
      url: img.url,
      label: img.title || img.file_name,
      source: 'product' as const,
    }));

const InfoStep: React.FC<InfoStepProps> = ({
  project,
  onSave,
  onGenerateCastCandidates,
  isCastCandidatesRequesting = false,
}) => {
  // 各フィールドはマウント時（＝このタブを開いた時点）のprojectから初期化するローカルドラフト。
  // 他ステップの下書き（シーン設定）と同じく、保存操作のときだけサーバーへ送る。
  const [title, setTitle] = useState(project.title);
  const [settings, setSettings] = useState<UgcAdSettingsValue>(() => toSettingsValue(project));
  const [productId, setProductId] = useState<number | null>(project.product_id);
  const [productName, setProductName] = useState<string | undefined>(undefined);
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [imageItems, setImageItems] = useState<UgcAdImageKeyItem[]>([]);

  // 出演者リファレンス（顔の一貫性アンカー）。null＝おまかせ（構成案フィックス時に自動確定）
  const [castImageKey, setCastImageKey] = useState<string | null>(project.cast_image_key);
  const [uploadedCastCandidates, setUploadedCastCandidates] = useState<UgcAdImageKeyItem[]>([]);
  const [isUploadingCast, setIsUploadingCast] = useState(false);
  const [castUploadError, setCastUploadError] = useState<string | null>(null);
  const castFileInputRef = useRef<HTMLInputElement>(null);

  // CTAロゴの明示指定。null＝自動（backendが brand_logo → product_image_keys[0] の順で解決）
  const [logoImageKey, setLogoImageKey] = useState<string | null>(project.advanced_settings?.logo_image_key ?? null);
  const [brandLogoCandidates, setBrandLogoCandidates] = useState<UgcAdImageKeyItem[]>([]);
  const [uploadedLogoCandidates, setUploadedLogoCandidates] = useState<UgcAdImageKeyItem[]>([]);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // 保存ガードの判定基準（マウント時点の値。影響グループの差分検出に使う）
  const initialRef = useRef({
    settings: toSettingsValue(project),
    productId: project.product_id,
    productImageKeysSorted: sortedKey(project.product_image_keys),
    castImageKey: project.cast_image_key,
  });
  const hasScenes = (project.scenes ?? []).length > 0;

  // 既存の product_id / product_image_keys から、表示できる範囲で初期状態を復元する。
  // アップロードのみで追加された画像はプレビューURLを復元できないため、汎用アイコン表示にする。
  useEffect(() => {
    let cancelled = false;

    const restoreImagesWithoutProduct = () => {
      if (project.product_image_keys.length === 0) return;
      setImageItems(
        project.product_image_keys.map((key) => ({
          key,
          url: null,
          label: 'アップロード画像',
          source: 'upload' as const,
        }))
      );
    };

    const load = async () => {
      if (!project.product_id) {
        restoreImagesWithoutProduct();
        return;
      }
      try {
        const product = await knowledgeApiService.getProduct(project.product_id);
        if (cancelled) return;
        setProductName(product.name);
        const items: UgcAdImageKeyItem[] = project.product_image_keys.map((key) => {
          const match = product.product_images?.find((img) => img.file_path === key);
          return match
            ? { key, url: match.url, label: match.title || match.file_name, source: 'product' as const }
            : { key, url: null, label: 'アップロード画像', source: 'upload' as const };
        });
        setImageItems(items);
        setBrandLogoCandidates(extractBrandLogoImages(product.product_images));
      } catch {
        // 商品取得に失敗してもキー自体は失わない（プレビュー無しで表示）
        if (!cancelled) restoreImagesWithoutProduct();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // マウント時（＝このタブを開いた時点）にのみ初期化する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productImageKeys = useMemo(() => imageItems.map((i) => i.key), [imageItems]);

  const handleSettingsChange = (patch: Partial<UgcAdSettingsValue>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  /** 商品選択モーダルの確定時。商品由来の画像だけを新しい選択で置き換え、アップロード画像は残す */
  const handleProductSelected = (data: ProductSelectionData) => {
    setProductId(data.product.id);
    setProductName(data.product.name);
    setShowProductSelectionModal(false);
    setImageItems((prev) => {
      const uploadsOnly = prev.filter((i) => i.source !== 'product');
      const productItems: UgcAdImageKeyItem[] = data.selectedImages.map((img) => ({
        key: img.file_path,
        url: img.url,
        label: img.title || img.file_name,
        source: 'product',
      }));
      return [...productItems, ...uploadsOnly];
    });
    setBrandLogoCandidates(extractBrandLogoImages(data.product.product_images));
  };

  /** ロゴ候補の一覧（ロゴ画像＋現在選択中の画像＋アップロードしたロゴ。重複キーは先勝ち） */
  const logoCandidates = useMemo(() => {
    const merged = [...brandLogoCandidates, ...imageItems, ...uploadedLogoCandidates];
    const seen = new Set<string>();
    return merged.filter((item) => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });
  }, [brandLogoCandidates, imageItems, uploadedLogoCandidates]);

  const savedLogoImageKey = project.advanced_settings?.logo_image_key ?? null;
  const selectedLogoCandidate = logoCandidates.find((c) => c.key === logoImageKey) ?? null;
  const resolvedLogoPreviewUrl =
    selectedLogoCandidate?.url ?? (logoImageKey === savedLogoImageKey ? project.cta?.logo_url ?? null : null);

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploadError(null);

    if (!file.type.startsWith('image/')) {
      setLogoUploadError(`${file.name} は画像ファイルではないためアップロードできません`);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setLogoUploadError(`${file.name} は10MBを超えているためアップロードできません`);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
      return;
    }

    setIsUploadingLogo(true);
    try {
      const { s3Key } = await ugcAdService.uploadImage(file);
      setUploadedLogoCandidates((prev) => [
        ...prev,
        { key: s3Key, url: URL.createObjectURL(file), label: file.name, source: 'upload' },
      ]);
      setLogoImageKey(s3Key);
    } catch (err) {
      setLogoUploadError(`${file.name} のアップロードに失敗しました（${ugcAdService.extractErrorMessage(err)}）`);
    } finally {
      setIsUploadingLogo(false);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
    }
  };

  const buildParams = (): UpdateUgcAdProjectParams => ({
    title: title.trim() || undefined,
    product_id: productId,
    product_image_keys: productImageKeys,
    format_type: settings.formatType,
    cast_gender: settings.castGender,
    cast_age: settings.castAge,
    cast_image_key: castImageKey,
    target_seconds: settings.targetSeconds,
    free_text: settings.freeText.trim() || undefined,
    advanced_settings: {
      required_words: splitWordsInput(settings.requiredWordsText),
      banned_words: splitWordsInput(settings.bannedWordsText),
      // CTA文言の編集は⑤プレビューに一本化（二重管理の解消）。ここでは現在値を保持するだけ
      cta: project.advanced_settings?.cta ?? null,
      logo_image_key: logoImageKey,
      // テロップと音声の合わせ方は⑤プレビュー側で切り替える。ここでは現在値を保持するだけ
      caption_sync: project.advanced_settings?.caption_sync ?? 'proportional',
    },
  });

  /**
   * 保存対象の変更が「構成案に効くグループ」または「映像に効くグループ」に触れているかを判定する
   * （title/cta/logo は無害グループなのでここでは見ない）。
   */
  const needsConfirm = (): boolean => {
    const initial = initialRef.current;

    const scriptChanged =
      settings.formatType !== initial.settings.formatType ||
      settings.castGender !== initial.settings.castGender ||
      settings.castAge !== initial.settings.castAge ||
      settings.targetSeconds !== initial.settings.targetSeconds ||
      settings.freeText.trim() !== initial.settings.freeText.trim() ||
      settings.requiredWordsText !== initial.settings.requiredWordsText ||
      settings.bannedWordsText !== initial.settings.bannedWordsText;

    const visualChanged =
      productId !== initial.productId ||
      sortedKey(productImageKeys) !== initial.productImageKeysSorted ||
      castImageKey !== initial.castImageKey;

    return (scriptChanged || visualChanged) && hasScenes;
  };

  /* ---------- 出演者リファレンス（顔の一貫性アンカー） ---------- */

  const isCastGenerating =
    isCastCandidatesRequesting ||
    project.cast_candidates_status === 'pending' ||
    project.cast_candidates_status === 'processing';

  /**
   * 出演者候補の一覧（確定済み画像＋生成候補＋アップロード。重複キーは先勝ち）。
   * 生成候補は project prop から直接読む（ポーリングで完成が自動反映されるため、ローカルに持たない）
   */
  const castCandidates = useMemo(() => {
    const confirmed: UgcAdImageKeyItem[] =
      project.cast_image_key && project.cast_image_url
        ? [{ key: project.cast_image_key, url: project.cast_image_url, label: '確定済みの出演者', source: 'upload' as const }]
        : [];
    const generated: UgcAdImageKeyItem[] = project.cast_candidates.map((c, i) => ({
      key: c.s3_key,
      url: c.url,
      label: `候補${i + 1}`,
      source: 'product' as const,
    }));
    const merged = [...confirmed, ...generated, ...uploadedCastCandidates];
    const seen = new Set<string>();
    return merged.filter((item) => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });
  }, [project.cast_image_key, project.cast_image_url, project.cast_candidates, uploadedCastCandidates]);

  const handleCastFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCastUploadError(null);

    if (!file.type.startsWith('image/')) {
      setCastUploadError(`${file.name} は画像ファイルではないためアップロードできません`);
      if (castFileInputRef.current) castFileInputRef.current.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setCastUploadError(`${file.name} は10MBを超えているためアップロードできません`);
      if (castFileInputRef.current) castFileInputRef.current.value = '';
      return;
    }

    setIsUploadingCast(true);
    try {
      const { s3Key } = await ugcAdService.uploadImage(file);
      setUploadedCastCandidates((prev) => [
        ...prev,
        { key: s3Key, url: URL.createObjectURL(file), label: file.name, source: 'upload' },
      ]);
      setCastImageKey(s3Key);
    } catch (err) {
      setCastUploadError(`${file.name} のアップロードに失敗しました（${ugcAdService.extractErrorMessage(err)}）`);
    } finally {
      setIsUploadingCast(false);
      if (castFileInputRef.current) castFileInputRef.current.value = '';
    }
  };

  /** UgcAdSettingsForm の出演者（性別・年代）直後に差し込むリファレンスピッカー */
  const castReferenceSlot = (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">出演者の顔（任意）</label>
      <p className="text-[11px] text-gray-400 mb-2">
        未選択なら構成案の確定時に自動で1人決まります。
      </p>
      <div className="flex flex-wrap gap-2 mb-2">
        <button
          type="button"
          disabled={isSaving}
          onClick={() => setCastImageKey(null)}
          title="おまかせ（構成案の確定時に自動で1人確定）"
          className={`w-20 h-20 rounded-lg border-2 flex items-center justify-center bg-white shrink-0 transition-colors disabled:opacity-50 ${
            castImageKey === null ? 'border-primary' : 'border-warm-border hover:border-primary/50'
          }`}
        >
          <span className="text-[10px] text-gray-500 px-1 text-center leading-tight">おまかせ</span>
        </button>
        {castCandidates.map((item) => (
          <button
            key={item.key}
            type="button"
            disabled={isSaving}
            onClick={() => setCastImageKey(item.key)}
            title={item.label}
            className={`w-20 h-20 rounded-lg overflow-hidden border-2 bg-white shrink-0 transition-colors disabled:opacity-50 ${
              castImageKey === item.key ? 'border-primary' : 'border-warm-border hover:border-primary/50'
            }`}
          >
            <img src={item.url ?? undefined} alt={`出演者候補: ${item.label}`} className="w-full h-full object-cover" />
          </button>
        ))}
        {isCastGenerating && (
          <div className="w-20 h-20 rounded-lg border-2 border-dashed border-warm-border flex flex-col items-center justify-center text-gray-400 shrink-0">
            <Loader2 className="w-5 h-5 animate-spin mb-1" />
            <span className="text-[9px]">生成中...</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={isSaving || isCastGenerating}
          onClick={() => void onGenerateCastCandidates(settings.castGender, settings.castAge)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-warm-border rounded-tasky-sm text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCastGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          性別・年代から候補を生成
        </button>
        <button
          type="button"
          disabled={isSaving || isUploadingCast}
          onClick={() => castFileInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-warm-border rounded-tasky-sm text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploadingCast ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          写真をアップロード
        </button>
        <input ref={castFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleCastFileChange} />
        <span className="text-[11px] text-gray-400">
          候補の生成は上で選んだ性別・年代で行い、画像生成分のクレジットを消費します
        </span>
      </div>
      {project.cast_candidates_status === 'failed' && (
        <p className="text-[11px] text-destructive mt-1">
          候補の生成に失敗しました{project.cast_candidates_error_message ? `（${project.cast_candidates_error_message}）` : ''}。もう一度お試しください。
        </p>
      )}
      {castUploadError && <p className="text-[11px] text-destructive mt-1">{castUploadError}</p>}
    </div>
  );

  const runSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const ok = await onSave(buildParams());
      if (!ok) setError('保存に失敗しました');
    } catch (e) {
      setError(ugcAdService.extractErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (needsConfirm()) {
      setShowConfirm(true);
      return;
    }
    runSave();
  };

  const handleConfirmedSave = () => {
    setShowConfirm(false);
    runSave();
  };

  return (
    <div className="space-y-5">
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      <TaskyCard variant="warm" padding="md">
        <h3 className="text-sm font-semibold text-dark mb-4">情報登録</h3>

        <div className="space-y-5">
          {/* タイトル（任意。空のまま保存すると自動命名のまま） */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">タイトル</label>
            <TaskyInput
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              disabled={isSaving}
              placeholder="未入力の場合は自動で名前が付きます"
            />
          </div>

          {/* 商品選択（任意） */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">商品選択（任意）</label>
            <div className="flex items-center gap-2 flex-wrap">
              <TaskyButton
                type="button"
                variant="outline"
                size="sm"
                disabled={isSaving}
                onClick={() => setShowProductSelectionModal(true)}
              >
                <Package className="w-3.5 h-3.5" />
                {productName ? '商品を変更' : '商品を選択する'}
              </TaskyButton>
              {productName && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                  {productName}
                </span>
              )}
            </div>
          </div>

          {/* 作成設定（構成タイプ・出演者・尺など）。仕上がりを決める判断なので、
              画像・CTAロゴといった細部より上に置く（レビューP1: 並び順の指摘） */}
          <UgcAdSettingsForm
            value={settings}
            onChange={handleSettingsChange}
            disabled={isSaving}
            productNamePlaceholder={productName}
            castReferenceSlot={castReferenceSlot}
          />

          {/* 画像（商品選択で選んだ画像＋アップロード。任意・最大3枚） */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">画像（任意・最大3枚）</label>
            <UgcAdImageKeysField items={imageItems} onChange={setImageItems} disabled={isSaving} />
          </div>

          {/* CTAロゴ（任意）。未指定なら商品のロゴ画像→選択画像の1枚目の順で自動的に使われる */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">CTAロゴ（任意）</label>
            <p className="text-[11px] text-gray-400 mb-2">
              未指定なら商品のロゴ画像→選択画像の1枚目の順で使います。
            </p>
            <div className="flex items-start gap-3">
              <div className="w-16 h-16 rounded-lg border border-warm-border bg-warm-surface flex items-center justify-center overflow-hidden shrink-0">
                {resolvedLogoPreviewUrl ? (
                  <img src={resolvedLogoPreviewUrl} alt="選択中のロゴ" className="w-full h-full object-contain p-1" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => setLogoImageKey(null)}
                    title="自動（商品のロゴ画像→選択画像の1枚目の順で自動選択）"
                    className={`w-14 h-14 rounded-lg border-2 flex items-center justify-center bg-white shrink-0 transition-colors disabled:opacity-50 ${
                      logoImageKey === null
                        ? 'border-primary'
                        : 'border-warm-border hover:border-primary/50'
                    }`}
                  >
                    <span className="text-[10px] text-gray-500 px-1 text-center leading-tight">自動</span>
                  </button>
                  {logoCandidates.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      disabled={isSaving}
                      onClick={() => setLogoImageKey(item.key)}
                      title={item.label}
                      className={`w-14 h-14 rounded-lg overflow-hidden border-2 flex items-center justify-center bg-white shrink-0 transition-colors disabled:opacity-50 ${
                        logoImageKey === item.key
                          ? 'border-primary'
                          : 'border-warm-border hover:border-primary/50'
                      }`}
                    >
                      {item.url ? (
                        <img
                          src={item.url}
                          alt={`ロゴ候補: ${item.label}`}
                          className="w-full h-full object-contain p-1"
                        />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    disabled={isSaving || isUploadingLogo}
                    onClick={() => logoFileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-warm-border rounded-tasky-sm text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploadingLogo ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Upload className="w-3 h-3" />
                    )}
                    ロゴをアップロード
                  </button>
                  <input
                    ref={logoFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoFileChange}
                  />
                </div>
                {logoUploadError && <p className="text-[11px] text-destructive mt-1">{logoUploadError}</p>}
              </div>
            </div>
          </div>

        </div>

        <div className="flex justify-end pt-4 mt-4 border-t border-warm-border">
          <TaskyButton onClick={handleSaveClick} isLoading={isSaving}>
            <ArrowRight className="w-4 h-4" />
            保存して次へ
          </TaskyButton>
        </div>
      </TaskyCard>

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}

      <TaskyConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmedSave}
        tone="warning"
        title="情報を変更しますか？"
        description="ここでの変更は、既に生成済みの構成案・ストーリーボード・クリップには自動的に反映されません（必要に応じて②構成案から作り直してください）。保存しますか？"
        confirmText="変更を保存する"
      />
    </div>
  );
};

export default InfoStep;
